import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ModalContextType {
  showUserTypeModal: boolean;
  showClientModal: boolean;
  showFreelancerModal: boolean;
  selectedUserType: string;
  openUserTypeModal: () => void;
  closeUserTypeModal: () => void;
  openClientModal: () => void;
  closeClientModal: () => void;
  openFreelancerModal: () => void;
  closeFreelancerModal: () => void;
  setSelectedUserType: (type: string) => void;
  handleUserTypeSelection: (type: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [showUserTypeModal, setShowUserTypeModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showFreelancerModal, setShowFreelancerModal] = useState(false);
  const [selectedUserType, setSelectedUserTypeState] = useState<string>('');

  const openUserTypeModal = () => setShowUserTypeModal(true);
  const closeUserTypeModal = () => setShowUserTypeModal(false);

  const openClientModal = () => setShowClientModal(true);
  const closeClientModal = () => setShowClientModal(false);

  const openFreelancerModal = () => setShowFreelancerModal(true);
  const closeFreelancerModal = () => setShowFreelancerModal(false);

  const setSelectedUserType = (type: string) => setSelectedUserTypeState(type);

  const handleUserTypeSelection = (type: string) => {
    setSelectedUserTypeState(type);
    closeUserTypeModal();
    if (type === 'Client') {
      openClientModal();
    } else if (type === 'Freelancer') {
      openFreelancerModal();
    }
  };

  const value: ModalContextType = {
    showUserTypeModal,
    showClientModal,
    showFreelancerModal,
    selectedUserType,
    openUserTypeModal,
    closeUserTypeModal,
    openClientModal,
    closeClientModal,
    openFreelancerModal,
    closeFreelancerModal,
    setSelectedUserType,
    handleUserTypeSelection,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};
